import { useEffect, useRef } from 'react';
import {
  computeInsideLayout,
  computeOutsideLayout,
  effectivePaper,
  type SpreadLayout,
} from '../lib/geometry';
import type {
  CardSize,
  Drift,
  MarkSettings,
  Orientation,
  PaperSize,
  PrinterSettings,
  SlotImage,
  SlotMap,
  SpreadFlip,
} from '../types';

interface Props {
  slots: SlotMap;
  cardSize: CardSize;
  paperSize: PaperSize;
  orientation: Orientation;
  drift: Drift;
  marks: MarkSettings;
  printer: PrinterSettings;
  insideEnabled?: boolean;
}

const PREVIEW_WIDTH_PORTRAIT = 460;
const PREVIEW_WIDTH_LANDSCAPE = 320;

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

const drawPanelImage = (
  ctx: CanvasRenderingContext2D,
  el: HTMLImageElement,
  rect: { x: number; y: number; width: number; height: number },
  rotate180: boolean
) => {
  if (rotate180) {
    ctx.save();
    ctx.translate(rect.x + rect.width / 2, rect.y + rect.height / 2);
    ctx.rotate(Math.PI);
    ctx.drawImage(el, -rect.width / 2, -rect.height / 2, rect.width, rect.height);
    ctx.restore();
  } else {
    ctx.drawImage(el, rect.x, rect.y, rect.width, rect.height);
  }
};

const drawSpread = async (
  canvas: HTMLCanvasElement,
  layout: SpreadLayout,
  effPaperWidthPx: number,
  effPaperHeightPx: number,
  canonicalFirst: SlotImage | undefined,
  canonicalSecond: SlotImage | undefined,
  flip: SpreadFlip,
  marks: MarkSettings
) => {
  const previewWidth =
    layout.orientation === 'landscape' ? PREVIEW_WIDTH_LANDSCAPE : PREVIEW_WIDTH_PORTRAIT;
  const scale = previewWidth / effPaperWidthPx;
  const dpr = window.devicePixelRatio || 1;
  const cssW = previewWidth;
  const cssH = Math.round(effPaperHeightPx * scale);
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);

  const styles = getComputedStyle(canvas);
  const paperFill = styles.getPropertyValue('--t-paper').trim() || '#fffaf0';
  const paperEdge = styles.getPropertyValue('--t-paper-edge').trim() || '#e0d4ba';

  ctx.fillStyle = paperFill;
  ctx.fillRect(0, 0, effPaperWidthPx, effPaperHeightPx);
  ctx.strokeStyle = paperEdge;
  ctx.lineWidth = 2 / scale;
  ctx.strokeRect(0, 0, effPaperWidthPx, effPaperHeightPx);

  // Apply swap across fold. The fold axis differs by orientation but the
  // semantics ("first image into second slot when swapped") are the same.
  const firstImg = flip.swapLR ? canonicalSecond : canonicalFirst;
  const secondImg = flip.swapLR ? canonicalFirst : canonicalSecond;

  if (firstImg) {
    const el = await loadImage(firstImg.previewUrl);
    drawPanelImage(ctx, el, layout.firstPanel, flip.rotate180);
  }
  if (secondImg) {
    const el = await loadImage(secondImg.previewUrl);
    drawPanelImage(ctx, el, layout.secondPanel, flip.rotate180);
  }

  const markColor = '#7a6a52';

  if (marks.foldLine) {
    ctx.strokeStyle = markColor;
    ctx.lineWidth = 1.5 / scale;
    ctx.setLineDash([18 / scale, 12 / scale]);
    ctx.beginPath();
    ctx.moveTo(layout.foldStart.x, layout.foldStart.y);
    ctx.lineTo(layout.foldEnd.x, layout.foldEnd.y);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  if (marks.cornerMarks) {
    ctx.strokeStyle = markColor;
    ctx.lineWidth = 1.2 / scale;
    for (const m of layout.cropMarks) {
      ctx.beginPath();
      ctx.moveTo(m.x1, m.y1);
      ctx.lineTo(m.x2, m.y2);
      ctx.stroke();
    }
  }

  if (marks.cardBorder) {
    ctx.strokeStyle = markColor;
    ctx.lineWidth = 1.0 / scale;
    ctx.setLineDash([10 / scale, 8 / scale]);
    ctx.strokeRect(layout.spreadX, layout.spreadY, layout.spreadW, layout.spreadH);
    ctx.setLineDash([]);
  }
};

export function SpreadPreview({
  slots,
  cardSize,
  paperSize,
  orientation,
  drift,
  marks,
  printer,
  insideEnabled,
}: Props) {
  const outsideRef = useRef<HTMLCanvasElement | null>(null);
  const insideRef = useRef<HTMLCanvasElement | null>(null);

  const hasInsideImg = Boolean(slots.insideLeft || slots.insideRight);
  const renderInside = insideEnabled === undefined ? hasInsideImg : insideEnabled;

  const effP = effectivePaper(paperSize, orientation);

  useEffect(() => {
    if (outsideRef.current) {
      const layout = computeOutsideLayout(paperSize, cardSize, orientation);
      void drawSpread(
        outsideRef.current,
        layout,
        effP.widthPx,
        effP.heightPx,
        slots.back,
        slots.front,
        printer.outside,
        marks
      );
    }
  }, [slots.back, slots.front, cardSize, paperSize, orientation, marks, printer.outside, effP.widthPx, effP.heightPx]);

  useEffect(() => {
    if (renderInside && insideRef.current) {
      const layout = computeInsideLayout(paperSize, cardSize, drift, orientation);
      void drawSpread(
        insideRef.current,
        layout,
        effP.widthPx,
        effP.heightPx,
        slots.insideLeft,
        slots.insideRight,
        printer.inside,
        marks
      );
    }
  }, [slots.insideLeft, slots.insideRight, cardSize, paperSize, orientation, drift, marks, printer.inside, renderInside, effP.widthPx, effP.heightPx]);

  const insideLabel =
    orientation === 'landscape' ? 'inside-top · inside-bottom' : 'inside-left · inside-right';
  const insidePromptLabel =
    orientation === 'landscape'
      ? 'drop inside-top or inside-bottom to fill these panels'
      : 'drop inside-left or inside-right to fill these panels';

  return (
    <section className="card-section">
      <h2>3. Preview</h2>
      <div className="preview-row">
        <figure className="preview-fig">
          <figcaption className="preview-title">Outside (page 1)</figcaption>
          <canvas ref={outsideRef} className="preview-canvas" />
          <figcaption className="preview-sub">back · front</figcaption>
        </figure>
        {renderInside && (
          <figure className="preview-fig">
            <figcaption className="preview-title">Inside (page 2)</figcaption>
            <canvas ref={insideRef} className="preview-canvas" />
            <figcaption className="preview-sub">
              {hasInsideImg ? insideLabel : insidePromptLabel}
            </figcaption>
          </figure>
        )}
      </div>
    </section>
  );
}
